import { inject, Observer } from "mobx-react"
import Head from "next/head"
import React from "react"

import CourseApplyDoneModal from "~/components/organisms/CourseApplyDoneModal"
import CourseInputPersonalDataModal from "~/components/organisms/CourseInputPersonalDataModal"
import CourseSchduleSelectModal from "~/components/organisms/CourseSchduleSelectModal"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import Background from "~/components/organisms/course-detail/Background"
import Curriculum from "~/components/organisms/course-detail/Curriculum"
import Feature from "~/components/organisms/course-detail/Feature"
import LearningStyle from "~/components/organisms/course-detail/LearningStyle"
import MainVisual from "~/components/organisms/course-detail/MainVisual"
// import Consultation from "~/components/organisms/course-detail/Consultation"
import Price from "~/components/organisms/course-detail/Price"
import PromoteArea from "~/components/organisms/course-detail/PromoteArea"
import { Course } from "~/entities/Course"
import { TmpUserInfo } from "~/entities/User"
import { staticPath } from "~/services/helper"
import ApplicationStore from "~/stores/ApplicationStore"
import RootStore from "~/stores/RootStore"
import UiStore from "~/stores/UiStore"
import UserStore from "~/stores/UserStore"

interface Props {
  applicationStore: ApplicationStore
  course: Course
  uiStore: UiStore
  userStore: UserStore
}

interface State {
  selectedScheduleValues: string[]
  isLoading: boolean
  tmpUserInfo: TmpUserInfo
}

@inject("applicationStore", "uiStore", "userStore")
class InformationTechnology extends React.Component<Props, State> {
  static async getInitialProps({ initialStoreData }: { initialStoreData: RootStore }) {
    const { courseStore } = initialStoreData
    const course = await courseStore.fetchById(7)
    return { course }
  }

  state = {
    selectedScheduleValues: [],
    isLoading: false,
    tmpUserInfo: {
      name: null,
      phoneNumber: null,
    },
  }

  render() {
    const { applicationStore, uiStore, userStore, course } = this.props
    const { selectedScheduleValues, isLoading, tmpUserInfo } = this.state
    const handleSelectSchedule = (schedules: string[]) => {
      this.setState({
        selectedScheduleValues: schedules,
      })
    }

    const toggleLoading = () => {
      this.setState({
        isLoading: !this.state.isLoading,
      })
    }

    const updateTmpUserInfo = (values: TmpUserInfo) => {
      this.setState({
        tmpUserInfo: values,
      })
    }

    const mainActionForSchduleSelectModal = async () => {
      toggleLoading()
      selectedScheduleValues.sort()
      if (userStore.authed) {
        await applicationStore.courseConsultationApply(7, selectedScheduleValues.sort())
      } else {
        await applicationStore.courseConsultationApplyByGuest(7, tmpUserInfo, selectedScheduleValues.sort())
      }
      uiStore.toggleCourseSchduleSelectModal()
      uiStore.toggleCompleteApplyCourseModal()
      toggleLoading()
    }

    return (
      <>
        <Head>
          <title>IT基礎スキル講座 - ビジネス職・総合職志望の学生向け | Infra（インフラ）</title>
          <meta name="description" content={course.description} />
          <meta name="og:title" content="IT基礎スキル講座 - ビジネス職・総合職志望の学生向け | Infra（インフラ）" />
          <meta
            name="og:description"
            content="本講座では総合職やビジネス職を志望する学生向けに実務で必要となるデジタルリテラシー・IT基礎スキルをゼロから学びます。"
          />
          <meta
            name="og:image"
            content={process.env.APP_HOST + staticPath("/static/images/course/information-technology/ogp.png")}
          />
        </Head>
        <Header logoType="Courses" showTab />
        <MainVisual
          disabled
          mSrcPc={staticPath("/static/images/course/information-technology/mv.png")}
          mSrcSp={staticPath("/static/images/course/information-technology/mv-sp.png")}
          typoPath={staticPath("/static/images/course/information-technology/typo.svg")}
          heading={"IT基礎スキル講座"}
          subHeading={"ビジネス職・総合職志望の学生向け"}
          description=""
          courseLineUrl={course.lineUrl}
        />
        <Background
          message={`新型コロナウイルスによるテレワーク、オンライン授業、ネット購買、電子決済、料理の宅配など消費者行動のデジタルシフトが急速に進んでいます。そして数多くの企業が「DX(デジタルトランスフォーメーション)」推進に乗り出しており、これからのデジタル社会においては誰もが「最低限のIT知識」を持つことが求められます。
          本講座では総合職やビジネス職を志望する学生向けに実務で必要となるデジタルリテラシー・IT基礎スキルをゼロから学びます。`}
          items={[
            {
              imgNumberPc: staticPath("/static/images/course/number-1-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-1-sp.svg"),
              heading: "企業経営者の多くがDX推進の課題として人材不足を挙げている",
              description:
                "DX推進の課題については、「人材育成が進んでいない」（34.5％）、「社内でDX推進をリードできる人材がいない」（24.3％）など社内の人材不足や人材育成が課題になっている状況も見受けられた。",
              linkURL:
                "https://www.vmware.com/jp/company/news/releases/2020/vmw-covid-19-digital-transformation_072020.html",
              linkText: "vmware：情報システム／IT予算の傾向やDXに関する調査結果より",
            },
            {
              imgNumberPc: staticPath("/static/images/course/number-2-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-2-sp.svg"),
              heading: "新卒採用において以前よりも\n重要度が増した「ITスキル」",
              description:
                "就職人気企業では新卒採用の現場において、学生の「デジタルリテラシー（IT を使いこなせる能力）」への重視度が⾼まっています。",
              linkURL:
                "https://www.adobe.com/jp/news-room/news/202007/20200729_adobe-survey-of-skills-required-for-new-graduates.html",
              linkText: "アドビ：新卒採用で企業が重視するスキルに関する調査結果より",
            },
            {
              imgNumberPc: staticPath("/static/images/course/number-3-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-3-sp.svg"),
              heading: "文系社員にAI研修を実施する大手企業が増加",
              description:
                "三菱商事や住友商事、日立、ダイキン工業など様々な大手企業が営業職や事務系など非技術職の社員に対してAI研修を実施しており、全員が持つべき「リテラシー」と考える企業が出てきています。",
              linkURL: "https://www.nikkei.com/article/DGXMZO65089790W0A011C2TJ1000/",
              linkText: "日本経済新聞：商社、全社員対象にAI研修「文系社員」をDX",
            },
          ]}
        />
        <PromoteArea
          disabled
          bgPath={staticPath("/static/images/course/information-technology/mv.png")}
          courseName="IT基礎スキル講座"
          courseLineUrl={course.lineUrl}
          discount={1}
        />
        <Feature
          items={[
            {
              imgNumberPc: staticPath("/static/images/course/number-1-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-1-sp.svg"),
              heading: "エクセルやプログラミング、AIリテラシーなど必要なIT基礎スキルを一度に",
              description:
                "本講座では専門職以外の職でも必要とされるITスキル（ITを使いこなす能力）を幅広く学習いただきます。",
            },
            {
              imgNumberPc: staticPath("/static/images/course/number-2-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-2-sp.svg"),
              heading: "ビジネス職・総合職を志望する学生に向けたカリキュラム",
              description:
                "ITツールやプログラミングの初心者・未経験者に向けて必要最低限のIT知識/スキルを得られる講座をご用意しました。",
            },
          ]}
        />
        <PromoteArea
          disabled
          bgPath={staticPath("/static/images/course/information-technology/mv.png")}
          courseName="IT基礎スキル講座"
          courseLineUrl={course.lineUrl}
          discount={1}
        />
        <Curriculum
          heading="デジタル社会を生き抜くITスキルを6週間で（オンライン完結）"
          period="2020年12月中旬より開講予定"
          curriculumItems={[
            {
              periodImgPc: staticPath("/static/images/course/period-6weeks-pc.svg"),
              periodImgSp: staticPath("/static/images/course/period-6weeks-sp.svg"),
              heading: "全6回",
              date: "",
              caption: "1〜2. 実務レベルのエクセルスキルを身につけよう",
              body: "エクセルの関数、グラフ作成、ピボットテーブルなど実務で用いるExcelの使い方を学習します。",
            },
            {
              caption: "3〜4. エクセルでプログラミングをしよう",
              body: "エクセルのマクロを組むプログラミング言語VBAについて学習します。定型的で複雑な計算業務をボタンクリックひとつで全自動で実行するプログラムを構築します。",
            },
            {
              caption: "5. AI・データリテラシーを学ぼう",
              body: "AIにできること・できないことを理解し、AIをビジネスに活かすためのリテラシーを養います。またデータ活用スキルとして一定のデータ分析や統計学の知識を得ます。",
            },
            {
              caption: "6. PythonでAIを実行しよう",
              body: "AI（人工知能）開発によく用いられるプログラミング言語Pythonを用いて、AIの根幹となる技術である機械学習を実施します。",
            },
          ]}
          recommendItems={[
            "就活・入社までに最低限のITスキルを身につけておきたい",
            "ITスキルを身に付けたいが、何を学習すれば良いかわからない",
            "IT業界で働くことを見据えてプログラミング知識を身につけておきたい",
          ]}
        />
        <PromoteArea
          disabled
          bgPath={staticPath("/static/images/course/information-technology/mv.png")}
          courseName="IT基礎スキル講座"
          courseLineUrl={course.lineUrl}
          discount={1}
        />
        <LearningStyle />
        <PromoteArea
          disabled
          bgPath={staticPath("/static/images/course/information-technology/mv.png")}
          courseName="IT基礎スキル講座"
          courseLineUrl={course.lineUrl}
          discount={1}
        />
        <Price
          items={[
            {
              price: 87000,
              // originalPrice: 107000,
              splitNum: 6,
            },
          ]}
        />
        <PromoteArea
          disabled
          bgPath={staticPath("/static/images/course/information-technology/mv.png")}
          courseName="IT基礎スキル講座"
          courseLineUrl={course.lineUrl}
          discount={1}
        />
        <Observer>
          {() => (
            <>
              <CourseInputPersonalDataModal
                open={uiStore.authPromptModalOpen}
                close={() => uiStore.toggleAuthPromptModal()}
                updateTmpUserInfo={updateTmpUserInfo}
                mainAction={() => {
                  uiStore.toggleAuthPromptModal()
                  uiStore.toggleCourseSchduleSelectModal()
                }}
              />
              <CourseSchduleSelectModal
                open={uiStore.courseSchduleSelectModalOpen}
                selectedScheduleValues={selectedScheduleValues}
                onSelectSchedule={handleSelectSchedule}
                mainAction={mainActionForSchduleSelectModal}
                subAction={() => uiStore.toggleCourseSchduleSelectModal()}
                isLoading={isLoading}
              />
              <CourseApplyDoneModal
                open={uiStore.completeApplyCourseModalOpen}
                close={() => uiStore.toggleCompleteApplyCourseModal()}
                addFriend={() => window.open(course.lineUrl)}
                lineQrImageUrl={course.lineQrImageUrl}
              />
            </>
          )}
        </Observer>
        <Footer />
      </>
    )
  }
}

export default InformationTechnology
